import React, { useState, useEffect, useCallback, useRef } from 'react';
import AgentTutorial from './agents/AgentTutorial';
import {
  Bot,
  Plus,
  Settings,
  Play,
  Pause,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  MessageSquare,
  Mail,
  Phone,
  Smartphone,
  Zap,
  Brain,
  Target,
  TrendingUp,
  FileText,
  FolderOpen,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCheck,
  X,
  Save,
  Loader2,
  Sparkles,
  Users,
  Building2,
  Calendar,
  BarChart3,
  Webhook,
  TestTube,
  BookOpen,
  Upload,
  FileUp,
  Rocket,
  Globe,
  GraduationCap,
  FileSpreadsheet,
  FilePlus,
  Image,
  Video,
  Wand2,
  FileTemplate,
  Paperclip,
  MailPlus,
  Sparkle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../services/api';
import EmpresaSearchSelect from './EmpresaSearchSelect';
// Note: AgentChatModal exists at ./agents/AgentChatModal.jsx but we use the inline dialog for now

const AGENT_TYPES = [
  { value: 'sales', label: 'Ventas', icon: Target, color: 'text-green-500', description: 'Seguimiento de leads y cierre de ventas' },
  { value: 'support', label: 'Soporte', icon: MessageSquare, color: 'text-blue-500', description: 'Atención al cliente y resolución de dudas' },
  { value: 'marketing', label: 'Marketing', icon: TrendingUp, color: 'text-purple-500', description: 'Contenido y campañas promocionales' },
  { value: 'assistant', label: 'Asistente', icon: Brain, color: 'text-amber-500', description: 'Ayuda general con tareas del CRM' },
  { value: 'custom', label: 'Personalizado', icon: Settings, color: 'text-gray-500', description: 'Agente con configuración personalizada' }
];

const CHANNELS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: Smartphone },
  { value: 'sms', label: 'SMS', icon: Phone }
];

const TRIGGERS = [
  { value: 'manual', label: 'Manual', description: 'Activado manualmente' },
  { value: 'task_created', label: 'Tarea Creada', description: 'Cuando se crea una tarea' },
  { value: 'task_due', label: 'Tarea Vencida', description: 'Cuando una tarea está por vencer' },
  { value: 'lead_no_response', label: 'Sin Respuesta', description: 'Cuando un lead no responde en X días' },
  { value: 'form_submitted', label: 'Formulario', description: 'Cuando se envía un formulario' },
  { value: 'n8n_webhook', label: 'n8n Webhook', description: 'Activado por n8n' },
  { value: 'scheduled', label: 'Programado', description: 'Ejecutado según horario' }
];

const TONES = [
  { value: 'profesional', label: 'Profesional' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
  { value: 'amigable', label: 'Amigable' },
  { value: 'directo', label: 'Directo' }
];

const GOOGLE_TOOLS = [
  { id: 'gmail', label: 'Gmail', description: 'Enviar y leer emails' },
  { id: 'drive', label: 'Drive', description: 'Acceder a archivos' },
  { id: 'docs', label: 'Docs', description: 'Crear documentos' },
  { id: 'sheets', label: 'Sheets', description: 'Crear hojas de cálculo' },
  { id: 'calendar', label: 'Calendar', description: 'Gestionar eventos' }
];

const CAPABILITIES = [
  { id: 'send_emails', label: 'Enviar Emails', category: 'communication' },
  { id: 'send_whatsapp', label: 'Enviar WhatsApp', category: 'communication' },
  { id: 'create_tasks', label: 'Crear Tareas', category: 'crm' },
  { id: 'create_notes', label: 'Crear Notas', category: 'crm' },
  { id: 'create_deals', label: 'Crear Deals', category: 'crm' },
  { id: 'edit_empresas', label: 'Editar Empresas', category: 'crm' },
  { id: 'create_documents', label: 'Crear Documentos', category: 'documents' },
  { id: 'create_spreadsheets', label: 'Crear Hojas de Cálculo', category: 'documents' },
  { id: 'create_proposals', label: 'Crear Propuestas', category: 'documents' },
  { id: 'schedule_followups', label: 'Programar Seguimientos', category: 'automation' },
  { id: 'analyze_data', label: 'Analizar Datos', category: 'automation' }
];

const CRM_PAGES = [
  { id: 'all', label: 'Todas las páginas' },
  { id: 'empresas', label: 'Empresas' },
  { id: 'tareas', label: 'Tareas' },
  { id: 'embudos', label: 'Embudos' },
  { id: 'evaluaciones', label: 'Evaluaciones' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'reportes', label: 'Reportes' }
];

const AgentsPage = ({ currentUser }) => {
  const [agents, setAgents] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('agents');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showAgentChatModal, setShowAgentChatModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [agentChatMessages, setAgentChatMessages] = useState([]);
  const [agentChatInput, setAgentChatInput] = useState('');
  const [agentChatLoading, setAgentChatLoading] = useState(false);
  const agentChatEndRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'assistant',
    description: '',
    channels: ['email'],
    triggers: ['manual'],
    autonomy_level: 0,
    system_prompt: '',
    tone: 'profesional',
    language: 'es',
    max_actions_per_day: 50,
    max_message_length: 1000,
    assigned_to: [],
    webhook_url: '',
    // New fields
    is_global: false,
    global_pages: [],
    google_tools: {
      gmail: false,
      drive: false,
      docs: false,
      sheets: false,
      calendar: false
    },
    capabilities: {
      send_emails: true,
      send_whatsapp: false,
      create_tasks: true,
      create_notes: true,
      create_deals: false,
      edit_empresas: false,
      create_documents: false,
      create_spreadsheets: false,
      create_proposals: false,
      schedule_followups: true,
      analyze_data: false
    },
    memory_enabled: true,
    learning_enabled: true
  });
  
  // Generate message state
  const [generateData, setGenerateData] = useState({
    empresa_id: '',
    contact_id: '',
    channel: 'email',
    purpose: 'follow_up',
    additional_context: ''
  });
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Media generation state (Nano Banana & Veo)
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
  const [mediaPrompt, setMediaPrompt] = useState('');
  const [generatedMedia, setGeneratedMedia] = useState(null);
  const [generatingMedia, setGeneratingMedia] = useState(false);
  
  // Email Templates state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [emailForm, setEmailForm] = useState({
    template_id: '',
    empresa_id: '',
    contact_id: '',
    subject: '',
    body: '',
    variables: {},
    include_template_attachments: true,
    additional_attachments: []
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Template Assistant state
  const [showTemplateAssistant, setShowTemplateAssistant] = useState(false);
  const [assistantForm, setAssistantForm] = useState({
    template_type: 'seguimiento',
    purpose: '',
    tone: 'profesional',
    target_audience: '',
    key_points: []
  });
  const [generatedTemplate, setGeneratedTemplate] = useState(null);
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [newKeyPoint, setNewKeyPoint] = useState('');
  
  const [empresas, setEmpresas] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Fetch data
  const fetchAgents = useCallback(async () => {
    try {
      const response = await api.getAgents();
      setAgents(response.data || response);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Error al cargar agentes');
    }
  }, []);
  
  const fetchPendingActions = useCallback(async () => {
    try {
      const response = await api.getPendingActions();
      setPendingActions(response.data || response);
    } catch (error) {
      console.error('Error fetching pending actions:', error);
    }
  }, []);
  
  const fetchEmpresas = useCallback(async () => {
    try {
      const response = await api.getEmpresas();
      setEmpresas(response.data || response);
    } catch (error) {
      console.error('Error fetching empresas:', error);
    }
  }, []);
  
  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.getUsers();
      setUsers(response.data || response);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAgents(), fetchPendingActions(), fetchEmpresas(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAgents, fetchPendingActions, fetchEmpresas, fetchUsers]);
  
  // CRUD operations
  const handleCreateAgent = async () => {
    try {
      await api.createAgent(formData);
      toast.success('Agente creado exitosamente');
      setShowCreateModal(false);
      fetchAgents();
      resetForm();
    } catch (error) {
      toast.error('Error al crear agente');
    }
  };
  
  const handleUpdateAgent = async () => {
    try {
      await api.updateAgent(selectedAgent.id, formData);
      toast.success('Agente actualizado');
      setShowEditModal(false);
      fetchAgents();
    } catch (error) {
      toast.error('Error al actualizar agente');
    }
  };
  
  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este agente?')) return;
    
    try {
      await api.deleteAgent(agentId);
      toast.success('Agente eliminado');
      fetchAgents();
    } catch (error) {
      toast.error('Error al eliminar agente');
    }
  };
  
  const handleToggleStatus = async (agent) => {
    const newStatus = agent.status === 'active' ? 'paused' : 'active';
    try {
      await api.updateAgent(agent.id, { status: newStatus });
      toast.success(`Agente ${newStatus === 'active' ? 'activado' : 'pausado'}`);
      fetchAgents();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };
  
  // Action operations
  const handleApproveAction = async (actionId, approved, editedContent = null, feedback = '') => {
    try {
      await api.approveAction(actionId, { approved, edited_content: editedContent, feedback });
      toast.success(approved ? 'Acción aprobada' : 'Acción rechazada');
      fetchPendingActions();
      setShowActionModal(false);
    } catch (error) {
      toast.error('Error al procesar acción');
    }
  };
  
  const handleExecuteAction = async (actionId) => {
    try {
      await api.executeAction(actionId);
      toast.success('Acción ejecutada');
      fetchPendingActions();
      fetchAgents();
    } catch (error) {
      toast.error('Error al ejecutar acción');
    }
  };
  
  // Generate message
  const handleGenerateMessage = async () => {
    if (!selectedAgent) return;
    
    setGenerating(true);
    try {
      const response = await api.generateAgentMessage(selectedAgent.id, { ...generateData, agent_id: selectedAgent.id });
      setGeneratedContent(response.data || response);
      toast.success('Mensaje generado');
    } catch (error) {
      toast.error('Error al generar mensaje');
    } finally {
      setGenerating(false);
    }
  };
  
  // Open chat with agent
  const openAgentChat = (agent) => {
    setSelectedAgent(agent);
    setAgentChatMessages([{
      id: '1',
      sender: 'agent',
      text: `¡Hola! Soy ${agent.name}. ¿En qué puedo ayudarte hoy?\n\nPuedo ayudarte con:\n• Enviar emails\n• Crear documentos en Drive\n• Crear hojas de cálculo\n• Generar imágenes con IA\n• Generar videos con IA\n• Crear tareas y notas\n• Subir archivos a mi carpeta de aprendizaje\n• Buscar en Drive\n\nEscríbeme lo que necesitas.`,
      timestamp: new Date()
    }]);
    setAgentChatInput('');
    setShowAgentChatModal(true);
  };

  // Send message to agent chat
  const handleAgentChatSend = async () => {
    if (!selectedAgent || !agentChatInput.trim() || agentChatLoading) return;
    
    const userMessage = agentChatInput.trim();
    setAgentChatInput('');
    
    // Add user message
    setAgentChatMessages(prev => [...prev, {
      id: Date.now().toString(),
      sender: 'user',
      text: userMessage,
      timestamp: new Date()
    }]);
    
    setAgentChatLoading(true);
    
    try {
      const response = await api.post(`/agents/${selectedAgent.id}/chat`, {
        message: userMessage,
        context: {
          page: 'agents',
          userId: currentUser?.id,
          userName: currentUser?.name
        }
      });
      
      const data = response.data || response;
      
      // Add agent response
      setAgentChatMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        sender: 'agent',
        text: data.response || 'Lo siento, no pude procesar tu solicitud.',
        timestamp: new Date(),
        action: data.action_executed ? data.action_type : null,
        actionResult: data.action_result
      }]);
      
      if (data.action_executed) {
        toast.success(`Acción ejecutada: ${data.action_type}`);
        fetchAgents();
        fetchPendingActions();
      }
    } catch (error) {
      console.error('Chat error:', error);
      setAgentChatMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        sender: 'agent',
        text: 'Lo siento, ocurrió un error. Por favor intenta de nuevo.',
        timestamp: new Date(),
        error: true
      }]);
    } finally {
      setAgentChatLoading(false);
    }
  };

  // Quick actions for chat
  const chatQuickActions = [
    { icon: Mail, label: 'Enviar Email', prompt: 'Ayúdame a enviar un email de seguimiento' },
    { icon: FileText, label: 'Crear Documento', prompt: 'Crea un documento en Google Docs' },
    { icon: FileSpreadsheet, label: 'Crear Sheet', prompt: 'Crea una hoja de cálculo en Google Sheets' },
    { icon: Image, label: 'Generar Imagen', prompt: 'Genera una imagen con IA' },
    { icon: Video, label: 'Generar Video', prompt: 'Genera un video promocional' },
    { icon: Upload, label: 'Subir Archivo', prompt: 'Quiero subir un archivo a tu carpeta de aprendizaje' },
    { icon: FolderOpen, label: 'Buscar en Drive', prompt: 'Busca archivos en Google Drive' },
    { icon: CheckCircle, label: 'Crear Tarea', prompt: 'Crea una tarea de seguimiento' }
  ];
  
  const handleCreateActionFromGenerated = async () => {
    if (!selectedAgent || !generatedContent) return;
    
    try {
      await api.createAgentAction(selectedAgent.id, {
        action_type: `send_${generateData.channel}`,
        target_empresa_id: generateData.empresa_id,
        target_contact_id: generateData.contact_id,
        channel: generateData.channel,
        content: generatedContent.content,
        trigger: 'manual'
      });
      toast.success('Acción creada y pendiente de aprobación');
      setShowAgentChatModal(false);
      setGeneratedContent(null);
      fetchPendingActions();
    } catch (error) {
      toast.error('Error al crear acción');
    }
  };
  
  // Execute immediately without approval
  const [executingImmediate, setExecutingImmediate] = useState(false);
  
  const handleExecuteImmediate = async () => {
    if (!selectedAgent || !generatedContent) return;
    
    setExecutingImmediate(true);
    try {
      const response = await api.executeAgentImmediate(selectedAgent.id, {
        action_type: `send_${generateData.channel}`,
        target_empresa_id: generateData.empresa_id,
        target_contact_id: generateData.contact_id,
        channel: generateData.channel,
        content: generatedContent.content
      });
      const result = response.data || response;
      
      if (result.success) {
        toast.success(`¡Mensaje enviado exitosamente! ${result.result?.status || ''}`);
        setShowAgentChatModal(false);
        setGeneratedContent(null);
        fetchAgents();
      } else {
        toast.error('Error al ejecutar: ' + (result.error || 'Error desconocido'));
      }
    } catch (error) {
      toast.error('Error al ejecutar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExecutingImmediate(false);
    }
  };
  
  // Media generation handlers (Nano Banana & Google Veo)
  const openMediaModal = (agent, type = 'image') => {
    setSelectedAgent(agent);
    setMediaType(type);
    setMediaPrompt('');
    setGeneratedMedia(null);
    setShowMediaModal(true);
  };
  
  const handleGenerateMedia = async () => {
    if (!selectedAgent || !mediaPrompt.trim()) return;
    
    setGeneratingMedia(true);
    setGeneratedMedia(null);
    
    try {
      let response;
      if (mediaType === 'image') {
        response = await api.post(`/agents/${selectedAgent.id}/generate-image`, {
          prompt: mediaPrompt,
          user_id: currentUser?.id
        });
      } else {
        response = await api.post(`/agents/${selectedAgent.id}/generate-video`, {
          prompt: mediaPrompt,
          user_id: currentUser?.id
        });
      }
      
      const result = response.data || response;
      
      if (result.success) {
        setGeneratedMedia(result);
        toast.success(mediaType === 'image' 
          ? '¡Imagen generada exitosamente!' 
          : 'Video en proceso de generación. Puede tardar varios minutos.'
        );
      } else {
        toast.error(`Error: ${result.error || 'Error desconocido'}`);
      }
    } catch (error) {
      toast.error(`Error al generar ${mediaType}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGeneratingMedia(false);
    }
  };
  
  // Email Templates functions
  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/agents/email-templates');
      const data = response.data || response;
      setEmailTemplates(data.templates || []);
      setTemplateVariables(data.variables || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };
  
  const openTemplateModal = (agent) => {
    setSelectedAgent(agent);
    setEmailForm({
      template_id: '',
      empresa_id: '',
      contact_id: '',
      subject: '',
      body: '',
      variables: {},
      include_template_attachments: true,
      additional_attachments: []
    });
    setSelectedTemplate(null);
    fetchEmailTemplates();
    setShowTemplateModal(true);
  };
  
  const handleTemplateSelect = async (templateId) => {
    if (templateId === "none") {
      setSelectedTemplate(null);
      setEmailForm(prev => ({
        ...prev,
        template_id: '',
        subject: '',
        body: ''
      }));
      return;
    }
    const template = emailTemplates.find(t => t.id === templateId);
    setSelectedTemplate(template);
    setEmailForm(prev => ({
      ...prev,
      template_id: templateId,
      subject: template?.subject || '',
      body: template?.body || ''
    }));
  };
  
  const handleSendTemplateEmail = async () => {
    if (!selectedAgent || !emailForm.empresa_id) {
      toast.error('Selecciona una empresa');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await api.post(`/agents/${selectedAgent.id}/send-email-template`, emailForm);
      const result = response.data || response;
      
      if (result.success) {
        toast.success('¡Email enviado exitosamente!');
        setShowTemplateModal(false);
      } else {
        toast.error(`Error: ${result.error || 'Error desconocido'}`);
      }
    } catch (error) {
      toast.error(`Error al enviar email: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Template Assistant functions
  const openTemplateAssistant = () => {
    setAssistantForm({
      template_type: 'seguimiento',
      purpose: '',
      tone: 'profesional',
      target_audience: '',
      key_points: []
    });
    setGeneratedTemplate(null);
    setShowTemplateAssistant(true);
  };
  
  const handleGenerateTemplate = async () => {
    if (!assistantForm.purpose.trim()) {
      toast.error('Describe el propósito del template');
      return;
    }
    
    setGeneratingTemplate(true);
    try {
      const response = await api.post('/agents/assist-template-creation', assistantForm);
      const result = response.data || response;
      
      if (result.success) {
        setGeneratedTemplate(result.generated_template);
        toast.success('¡Template generado exitosamente!');
      } else {
        toast.error(`Error: ${result.error || 'Error desconocido'}`);
      }
    } catch (error) {
      toast.error(`Error al generar template: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGeneratingTemplate(false);
    }
  };
  
  const handleSaveGeneratedTemplate = async () => {
    if (!generatedTemplate) return;
    
    try {
      const response = await api.post(`/agents/save-generated-template?created_by=${currentUser?.name || 'AI Assistant'}`, generatedTemplate);
      const result = response.data || response;
      
      if (result.success) {
        toast.success('¡Template guardado exitosamente!');
        setShowTemplateAssistant(false);
        setGeneratedTemplate(null);
        fetchEmailTemplates();
      } else {
        toast.error('Error al guardar template');
      }
    } catch (error) {
      toast.error(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  const addKeyPoint = () => {
    if (newKeyPoint.trim()) {
      setAssistantForm(prev => ({
        ...prev,
        key_points: [...prev.key_points, newKeyPoint.trim()]
      }));
      setNewKeyPoint('');
    }
  };
  
  const removeKeyPoint = (index) => {
    setAssistantForm(prev => ({
      ...prev,
      key_points: prev.key_points.filter((_, i) => i !== index)
    }));
  };
  
  // Training documents management
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [trainingData, setTrainingData] = useState([]);
  const [newTraining, setNewTraining] = useState({
    type: 'document',
    title: '',
    content: '',
    category: 'general',
    tags: [],
    file: null,
    file_name: '',
    file_type: ''
  });
  const [loadingTraining, setLoadingTraining] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const fetchTrainingData = async (agentId) => {
    setLoadingTraining(true);
    try {
      const response = await api.getAgentTraining(agentId);
      setTrainingData(response.data || response);
    } catch (error) {
      console.error('Error fetching training:', error);
    } finally {
      setLoadingTraining(false);
    }
  };
  
  const handleAddTraining = async () => {
    if (!selectedAgent || (!newTraining.content && !newTraining.file)) {
      toast.error('Ingresa contenido o sube un archivo');
      return;
    }
    
    try {
      // If there's a file, upload it first to Drive
      if (newTraining.file) {
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', newTraining.file);
        formData.append('type', newTraining.type);
        formData.append('title', newTraining.title || newTraining.file.name);
        formData.append('category', newTraining.category);
        formData.append('content', newTraining.content || '');
        
        await api.post(`/agents/${selectedAgent.id}/training/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Archivo subido al Drive del agente');
      } else {
        // Text-only training
        await api.addAgentTraining(selectedAgent.id, newTraining);
        toast.success('Documento de entrenamiento agregado');
      }
      
      fetchTrainingData(selectedAgent.id);
      setNewTraining({ 
        type: 'document', title: '', content: '', category: 'general', 
        tags: [], file: null, file_name: '', file_type: '' 
      });
    } catch (error) {
      toast.error('Error al agregar documento: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingFile(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Max 10MB
      if (file.size > 10 * 1024 * 1024) {
        toast.error('El archivo es muy grande. Máximo 10MB.');
        return;
      }
      setNewTraining(prev => ({
        ...prev,
        file: file,
        file_name: file.name,
        file_type: file.type,
        title: prev.title || file.name.replace(/\.[^/.]+$/, '')
      }));
    }
  };
  
  const handleDeleteTraining = async (trainingId) => {
    if (!selectedAgent) return;
    
    try {
      await api.deleteAgentTraining(selectedAgent.id, trainingId);
      toast.success('Documento eliminado');
      fetchTrainingData(selectedAgent.id);
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };
  
  const openTrainingModal = (agent) => {
    setSelectedAgent(agent);
    fetchTrainingData(agent.id);
    setShowTrainingModal(true);
  };
  
  // Memory & Learning functions
  const handleInitializeMemory = async (agentId) => {
    try {
      const response = await api.post(`/agents/${agentId}/memory/init`, { user_id: currentUser?.id });
      const result = response.data || response;
      if (result.success) {
        toast.success('Memoria del agente inicializada');
        if (result.document_url) {
          window.open(result.document_url, '_blank');
        }
        fetchAgents();
      } else {
        toast.error('Error al inicializar memoria');
      }
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleTriggerLearning = async (agentId) => {
    try {
      const response = await api.post(`/agents/${agentId}/learn`);
      const result = response.data || response;
      if (result.success) {
        toast.success('Aprendizaje completado! El agente ha actualizado sus patrones.');
      } else {
        toast.error('Error en el proceso de aprendizaje');
      }
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  // Drive integration
  const handleSetupDrive = async (agentId) => {
    try {
      await api.setupAgentDrive(agentId, currentUser.id);
      toast.success('Carpeta de Drive creada');
      fetchAgents();
    } catch (error) {
      toast.error('Error al configurar Drive');
    }
  };
  
  const handleSyncDrive = async (agentId) => {
    try {
      await api.syncAgentDrive(agentId, currentUser.id);
      toast.success('Datos sincronizados con Drive');
    } catch (error) {
      toast.error('Error al sincronizar');
    }
  };
  
  const handleTestWebhook = async (agentId, webhookUrl = null) => {
    try {
      const response = await api.testAgentWebhook(agentId, webhookUrl);
      const result = response.data || response;
      if (result.success) {
        toast.success('Webhook enviado exitosamente! Revisa n8n.');
      } else {
        // Show specific status if n8n responded but with error
        const status = result.result?.status;
        if (status) {
          if (status === 404) {
            toast.error(`n8n respondió 404 - Asegúrate de que el workflow esté escuchando (nodo Webhook activo)`);
          } else {
            toast.error(`Webhook enviado pero n8n respondió con error ${status}`);
          }
        } else {
          toast.error(`Error en webhook: ${result.result?.error || 'Error desconocido'}`);
        }
      }
    } catch (error) {
      toast.error('Error al probar webhook: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'assistant',
      description: '',
      channels: ['email'],
      triggers: ['manual'],
      autonomy_level: 0,
      system_prompt: '',
      tone: 'profesional',
      language: 'es',
      max_actions_per_day: 50,
      max_message_length: 1000,
      assigned_to: [],
      webhook_url: '',
      is_global: false,
      global_pages: [],
      google_tools: {
        gmail: false,
        drive: false,
        docs: false,
        sheets: false,
        calendar: false
      },
      capabilities: {
        send_emails: true,
        send_whatsapp: false,
        create_tasks: true,
        create_notes: true,
        create_deals: false,
        edit_empresas: false,
        create_documents: false,
        create_spreadsheets: false,
        create_proposals: false,
        schedule_followups: true,
        analyze_data: false
      },
      memory_enabled: true,
      learning_enabled: true
    });
  };
  
  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      name: agent.name,
      type: agent.type,
      description: agent.description || '',
      channels: agent.channels || ['email'],
      triggers: agent.triggers || ['manual'],
      autonomy_level: agent.autonomy_level || 0,
      system_prompt: agent.system_prompt || '',
      tone: agent.tone || 'profesional',
      language: agent.language || 'es',
      max_actions_per_day: agent.max_actions_per_day || 50,
      max_message_length: agent.max_message_length || 1000,
      assigned_to: agent.assigned_to || [],
      webhook_url: agent.webhook_url || '',
      is_global: agent.is_global || false,
      global_pages: agent.global_pages || [],
      google_tools: agent.google_tools || {
        gmail: false,
        drive: false,
        docs: false,
        sheets: false,
        calendar: false
      },
      capabilities: agent.capabilities || {
        send_emails: true,
        send_whatsapp: false,
        create_tasks: true,
        create_notes: true,
        create_deals: false,
        edit_empresas: false,
        create_documents: false,
        create_spreadsheets: false,
        create_proposals: false,
        schedule_followups: true,
        analyze_data: false
      },
      memory_enabled: agent.memory_enabled !== false,
      learning_enabled: agent.learning_enabled !== false
    });
    setShowEditModal(true);
  };
  
  const openGenerateModal = (agent) => {
    // Now opens the full chat modal instead of the old generate modal
    openAgentChat(agent);
  };
  
  const getAgentTypeInfo = (type) => AGENT_TYPES.find(t => t.value === type) || AGENT_TYPES[3];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-amber-500" size={48} />
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6" data-testid="agents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
            <Bot className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agentes de IA</h1>
            <p className="text-sm text-gray-500">Gestiona tus asistentes inteligentes</p>
          </div>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="bg-amber-500 hover:bg-amber-600"
          data-testid="create-agent-btn"
        >
          <Plus size={18} className="mr-2" />
          Nuevo Agente
        </Button>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Bot className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Agentes Activos</p>
              <p className="text-2xl font-bold">{agents.filter(a => a.status === 'active').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Acciones Pendientes</p>
              <p className="text-2xl font-bold">{pendingActions.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Acciones Hoy</p>
              <p className="text-2xl font-bold">{agents.reduce((sum, a) => sum + (a.actions_today || 0), 0)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Ejecutadas</p>
              <p className="text-2xl font-bold">{agents.reduce((sum, a) => sum + (a.successful_actions || 0), 0)}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="agents" className="flex items-center gap-2">
            <Bot size={16} />
            Agentes ({agents.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock size={16} />
            Pendientes ({pendingActions.length})
            {pendingActions.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {pendingActions.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="tutorial" className="flex items-center gap-2">
            <BookOpen size={16} />
            Tutorial
          </TabsTrigger>
        </TabsList>
        
        {/* Agents Tab */}
        <TabsContent value="agents">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map(agent => {
              const typeInfo = getAgentTypeInfo(agent.type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <div 
                  key={agent.id}
                  className={`bg-white rounded-xl border ${agent.status === 'active' ? 'border-gray-200' : 'border-gray-300 bg-gray-50'} shadow-sm overflow-hidden`}
                  data-testid={`agent-card-${agent.id}`}
                >
                  {/* Header */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${agent.status === 'active' ? 'bg-amber-100' : 'bg-gray-200'}`}>
                          <TypeIcon className={agent.status === 'active' ? typeInfo.color : 'text-gray-400'} size={20} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            {agent.name}
                            {agent.is_global && (
                              <Globe size={14} className="text-blue-500" title="Agente Global" />
                            )}
                          </h3>
                          <p className="text-xs text-gray-500">{typeInfo.label}</p>
                        </div>
                      </div>
                      <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                        {agent.status === 'active' ? 'Activo' : 'Pausado'}
                      </Badge>
                    </div>
                    {agent.description && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">{agent.description}</p>
                    )}
                    
                    {/* Google Tools & Capabilities Indicators */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {agent.google_tools?.gmail && <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200"><Mail size={10} className="mr-1" />Gmail</Badge>}
                      {agent.google_tools?.drive && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200"><FolderOpen size={10} className="mr-1" />Drive</Badge>}
                      {agent.google_tools?.docs && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200"><FileText size={10} className="mr-1" />Docs</Badge>}
                      {agent.google_tools?.sheets && <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200"><FileSpreadsheet size={10} className="mr-1" />Sheets</Badge>}
                      {agent.google_tools?.calendar && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200"><Calendar size={10} className="mr-1" />Calendar</Badge>}
                      {agent.capabilities?.create_proposals && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 border-amber-200"><FilePlus size={10} className="mr-1" />Propuestas</Badge>}
                      {agent.memory_enabled && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200"><Brain size={10} className="mr-1" />Memoria</Badge>}
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="px-4 py-3 bg-gray-50 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-gray-900">{agent.autonomy_level}%</p>
                      <p className="text-xs text-gray-500">Autonomía</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-amber-600">{agent.pending_actions || 0}</p>
                      <p className="text-xs text-gray-500">Pendientes</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">{agent.successful_actions || 0}</p>
                      <p className="text-xs text-gray-500">Ejecutadas</p>
                    </div>
                  </div>
                  
                  {/* Channels & Triggers */}
                  <div className="px-4 py-2 flex flex-wrap gap-1">
                    {agent.channels?.map(ch => (
                      <Badge key={ch} variant="outline" className="text-xs">
                        {ch === 'email' && <Mail size={10} className="mr-1" />}
                        {ch === 'whatsapp' && <Smartphone size={10} className="mr-1" />}
                        {ch === 'sms' && <Phone size={10} className="mr-1" />}
                        {ch}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Actions */}
                  <div className="p-3 border-t border-gray-100 flex flex-col gap-2">
                    {/* Primary action: Chat with Agent */}
                    <Button
                      size="sm"
                      onClick={() => openAgentChat(agent)}
                      className="w-full bg-[#D4A843] hover:bg-[#c49a3a] text-white"
                      disabled={agent.status !== 'active'}
                      data-testid={`chat-agent-${agent.id}`}
                    >
                      <MessageSquare size={14} className="mr-2" />
                      Chatear con {agent.name}
                    </Button>
                    
                    {/* Secondary actions row */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleStatus(agent)}
                        title={agent.status === 'active' ? 'Pausar' : 'Activar'}
                      >
                        {agent.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openGenerateModal(agent)}
                        className="flex-1"
                        disabled={agent.status !== 'active'}
                      >
                        <Sparkles size={14} className="mr-1" />
                        Generar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openTrainingModal(agent)}
                        title="Documentos de entrenamiento"
                      >
                        <BookOpen size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(agent)}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteAgent(agent.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Media Generation Buttons (Nano Banana & Veo) */}
                  <div className="px-3 pb-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openMediaModal(agent, 'image')}
                      disabled={agent.status !== 'active'}
                      className="flex-1 text-xs text-purple-600 hover:bg-purple-50"
                      title="Generar imagen con Nano Banana"
                      data-testid={`generate-image-${agent.id}`}
                    >
                      <Image size={12} className="mr-1" />
                      Generar Imagen
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openMediaModal(agent, 'video')}
                      disabled={agent.status !== 'active'}
                      className="flex-1 text-xs text-blue-600 hover:bg-blue-50"
                      title="Generar video con Google Veo"
                      data-testid={`generate-video-${agent.id}`}
                    >
                      <Video size={12} className="mr-1" />
                      Generar Video
                    </Button>
                  </div>
                  
                  {/* Email with Templates Button */}
                  <div className="px-3 pb-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openTemplateModal(agent)}
                      disabled={agent.status !== 'active'}
                      className="w-full text-xs text-emerald-600 hover:bg-emerald-50"
                      title="Enviar email usando template"
                      data-testid={`send-email-template-${agent.id}`}
                    >
                      <MailPlus size={12} className="mr-1" />
                      Enviar Email con Template
                    </Button>
                  </div>
                  
                  {/* Drive Integration */}
                  {!agent.drive_folder_id ? (
                    <div className="px-3 pb-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleSetupDrive(agent.id)}
                        className="w-full text-xs text-gray-500"
                      >
                        <FolderOpen size={12} className="mr-1" />
                        Configurar Drive para entrenamiento
                      </Button>
                    </div>
                  ) : (
                    <div className="px-3 pb-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleSyncDrive(agent.id)}
                        className="w-full text-xs text-green-600"
                      >
                        <RefreshCw size={12} className="mr-1" />
                        Sincronizar con Drive
                      </Button>
                    </div>
                  )}
                  
                  {/* Memory & Learning Buttons */}
                  {agent.memory_enabled && (
                    <div className="px-3 pb-3 flex gap-2">
                      {!agent.memory_document_id ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleInitializeMemory(agent.id)}
                          className="flex-1 text-xs text-purple-600 hover:bg-purple-50"
                        >
                          <Brain size={12} className="mr-1" />
                          Inicializar Memoria
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(`https://docs.google.com/document/d/${agent.memory_document_id}/edit`, '_blank')}
                          className="flex-1 text-xs text-purple-600 hover:bg-purple-50"
                        >
                          <Brain size={12} className="mr-1" />
                          Ver Memoria
                        </Button>
                      )}
                      {agent.learning_enabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleTriggerLearning(agent.id)}
                          className="flex-1 text-xs text-indigo-600 hover:bg-indigo-50"
                          title="Analizar acciones aprobadas y actualizar patrones"
                        >
                          <GraduationCap size={12} className="mr-1" />
                          Aprender
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Test Webhook Button */}
                  {agent.webhook_url && (
                    <div className="px-3 pb-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleTestWebhook(agent.id)}
                        className="w-full text-xs text-orange-600 hover:bg-orange-50"
                        data-testid={`test-webhook-${agent.id}`}
                      >
                        <TestTube size={12} className="mr-1" />
                        Probar Webhook n8n
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            
            {agents.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Bot className="mx-auto text-gray-300 mb-4" size={64} />
                <p className="text-gray-500">No hay agentes creados</p>
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4"
                  variant="outline"
                >
                  <Plus size={16} className="mr-2" />
                  Crear primer agente
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Pending Actions Tab */}
        <TabsContent value="pending">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            {pendingActions.length === 0 ? (
              <div className="text-center py-12">
                <CheckCheck className="mx-auto text-green-300 mb-4" size={64} />
                <p className="text-gray-500">No hay acciones pendientes de aprobación</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {pendingActions.map(action => (
                  <div 
                    key={action.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`pending-action-${action.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          action.action_type === 'send_email' ? 'bg-blue-100' :
                          action.action_type === 'send_whatsapp' ? 'bg-green-100' :
                          'bg-gray-100'
                        }`}>
                          {action.action_type === 'send_email' && <Mail className="text-blue-600" size={18} />}
                          {action.action_type === 'send_whatsapp' && <Smartphone className="text-green-600" size={18} />}
                          {action.action_type === 'add_note' && <FileText className="text-gray-600" size={18} />}
                          {action.action_type === 'create_task' && <Calendar className="text-purple-600" size={18} />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{action.agent_name}</span>
                            <Badge variant="outline" className="text-xs">{action.action_type}</Badge>
                            {action.risk_level === 'high' && (
                              <Badge variant="destructive" className="text-xs">Alto Riesgo</Badge>
                            )}
                          </div>
                          {action.empresa_name && (
                            <p className="text-sm text-gray-600">
                              <Building2 size={12} className="inline mr-1" />
                              {action.empresa_name}
                              {action.contact_name && ` → ${action.contact_name}`}
                            </p>
                          )}
                          {action.content?.subject && (
                            <p className="text-sm text-gray-700 mt-1">
                              <strong>Asunto:</strong> {action.content.subject}
                            </p>
                          )}
                          {action.content?.body && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {action.content.body}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(action.created_at).toLocaleString('es-ES')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedAction(action); setShowActionModal(true); }}
                        >
                          <Eye size={14} className="mr-1" />
                          Ver
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 hover:bg-green-50"
                          onClick={() => handleApproveAction(action.id, true)}
                        >
                          <CheckCircle size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleApproveAction(action.id, false)}
                        >
                          <XCircle size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tutorial Tab */}
        <TabsContent value="tutorial">
          <AgentTutorial onCreateAgent={() => { resetForm(); setShowCreateModal(true); }} />
        </TabsContent>
      </Tabs>
      
      {/* Create/Edit Agent Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={() => { setShowCreateModal(false); setShowEditModal(false); }}>
        <DialogContent className="max-w-2xl max-h-[min(800px,90vh)] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Bot className="text-amber-500" />
              {showEditModal ? 'Editar Agente' : 'Nuevo Agente de IA'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre del Agente *</Label>
                <Input
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Asistente de Ventas"
                  data-testid="agent-name-input"
                />
              </div>
              <div>
                <Label>Tipo de Agente</Label>
                <Select value={formData.type} onValueChange={val => setFormData(prev => ({ ...prev, type: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGENT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="flex items-center gap-2">
                          <t.icon size={14} className={t.color} />
                          {t.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="¿Qué hace este agente?"
                rows={2}
              />
            </div>
            
            {/* Channels */}
            <div>
              <Label>Canales de Comunicación</Label>
              <div className="flex gap-3 mt-2">
                {CHANNELS.map(ch => (
                  <label key={ch.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.channels.includes(ch.value)}
                      onChange={e => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, channels: [...prev.channels, ch.value] }));
                        } else {
                          setFormData(prev => ({ ...prev, channels: prev.channels.filter(c => c !== ch.value) }));
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <ch.icon size={16} />
                    <span className="text-sm">{ch.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Triggers */}
            <div>
              <Label>Disparadores</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {TRIGGERS.map(tr => (
                  <label key={tr.value} className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.triggers.includes(tr.value)}
                      onChange={e => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, triggers: [...prev.triggers, tr.value] }));
                        } else {
                          setFormData(prev => ({ ...prev, triggers: prev.triggers.filter(t => t !== tr.value) }));
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium">{tr.label}</span>
                      <p className="text-xs text-gray-500">{tr.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Global Agent Toggle */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Globe size={20} className="text-blue-500" />
                  <Label className="text-blue-700 font-medium">Agente Global</Label>
                </div>
                <Switch
                  checked={formData.is_global}
                  onCheckedChange={val => setFormData(prev => ({ ...prev, is_global: val }))}
                />
              </div>
              <p className="text-sm text-blue-600 mb-3">
                Los agentes globales pueden asistir a los usuarios en cualquier página del CRM
              </p>
              
              {formData.is_global && (
                <div>
                  <Label className="text-sm">Páginas donde aparecerá</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {CRM_PAGES.map(page => (
                      <label 
                        key={page.id} 
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer text-sm border transition-colors ${
                          formData.global_pages.includes(page.id) 
                            ? 'bg-blue-500 text-white border-blue-500' 
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.global_pages.includes(page.id)}
                          onChange={e => {
                            if (page.id === 'all') {
                              setFormData(prev => ({ ...prev, global_pages: e.target.checked ? ['all'] : [] }));
                            } else if (e.target.checked) {
                              setFormData(prev => ({ 
                                ...prev, 
                                global_pages: prev.global_pages.filter(p => p !== 'all').concat(page.id) 
                              }));
                            } else {
                              setFormData(prev => ({ 
                                ...prev, 
                                global_pages: prev.global_pages.filter(p => p !== page.id) 
                              }));
                            }
                          }}
                          className="hidden"
                        />
                        {page.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Google Tools */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-3">
                <FolderOpen size={20} className="text-green-600" />
                <Label className="text-green-700 font-medium">Herramientas de Google</Label>
              </div>
              <p className="text-sm text-green-600 mb-3">
                Permite al agente usar las integraciones de Google Workspace
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {GOOGLE_TOOLS.map(tool => (
                  <label 
                    key={tool.id}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border transition-colors ${
                      formData.google_tools[tool.id] 
                        ? 'bg-green-500 text-white border-green-500' 
                        : 'bg-white text-gray-700 border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.google_tools[tool.id]}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        google_tools: { ...prev.google_tools, [tool.id]: e.target.checked }
                      }))}
                      className="hidden"
                    />
                    <div>
                      <span className="text-sm font-medium">{tool.label}</span>
                      <p className={`text-xs ${formData.google_tools[tool.id] ? 'text-green-100' : 'text-gray-500'}`}>
                        {tool.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Capabilities */}
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <Zap size={20} className="text-amber-600" />
                <Label className="text-amber-700 font-medium">Capacidades del Agente</Label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {CAPABILITIES.map(cap => (
                  <label 
                    key={cap.id}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border transition-colors ${
                      formData.capabilities[cap.id] 
                        ? 'bg-amber-500 text-white border-amber-500' 
                        : 'bg-white text-gray-700 border-gray-300 hover:border-amber-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.capabilities[cap.id]}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        capabilities: { ...prev.capabilities, [cap.id]: e.target.checked }
                      }))}
                      className="hidden"
                    />
                    <span className="text-sm">{cap.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Memory & Learning */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <Brain size={20} className="text-purple-600" />
                <Label className="text-purple-700 font-medium">Memoria y Aprendizaje</Label>
              </div>
              <p className="text-sm text-purple-600 mb-3">
                Permite al agente mantener su propia memoria y aprender de las tareas aprobadas
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Memoria habilitada</span>
                    <p className="text-xs text-gray-500">El agente mantiene un documento con sus aprendizajes</p>
                  </div>
                  <Switch
                    checked={formData.memory_enabled}
                    onCheckedChange={val => setFormData(prev => ({ ...prev, memory_enabled: val }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Aprendizaje automático</span>
                    <p className="text-xs text-gray-500">Mejora basándose en tareas aprobadas y feedback</p>
                  </div>
                  <Switch
                    checked={formData.learning_enabled}
                    onCheckedChange={val => setFormData(prev => ({ ...prev, learning_enabled: val }))}
                  />
                </div>
              </div>
            </div>
            
            {/* Autonomy Level */}
            <div>
              <Label className="flex items-center justify-between">
                <span>Nivel de Autonomía: {formData.autonomy_level}%</span>
                <span className="text-xs text-gray-500">
                  {formData.autonomy_level === 0 && 'Todo requiere aprobación'}
                  {formData.autonomy_level > 0 && formData.autonomy_level < 50 && 'Bajo riesgo auto-aprobado'}
                  {formData.autonomy_level >= 50 && formData.autonomy_level < 90 && 'Riesgo medio auto-aprobado'}
                  {formData.autonomy_level >= 90 && 'Totalmente autónomo'}
                </span>
              </Label>
              <Slider
                value={[formData.autonomy_level]}
                onValueChange={([val]) => setFormData(prev => ({ ...prev, autonomy_level: val }))}
                max={100}
                step={10}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0% - Supervisado</span>
                <span>100% - Autónomo</span>
              </div>
            </div>
            
            {/* AI Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tono</Label>
                <Select value={formData.tone} onValueChange={val => setFormData(prev => ({ ...prev, tone: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Idioma</Label>
                <Select value={formData.language} onValueChange={val => setFormData(prev => ({ ...prev, language: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* System Prompt */}
            <div>
              <Label>Instrucciones del Sistema (System Prompt)</Label>
              <Textarea
                value={formData.system_prompt}
                onChange={e => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                placeholder="Instrucciones específicas para el comportamiento del agente..."
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                Deja vacío para usar el prompt predeterminado según el tipo de agente
              </p>
            </div>
            
            {/* Webhook URL */}
            {formData.triggers.includes('n8n_webhook') && (
              <div>
                <Label>Webhook URL (n8n)</Label>
                <Input
                  value={formData.webhook_url}
                  onChange={e => setFormData(prev => ({ ...prev, webhook_url: e.target.value }))}
                  placeholder="https://n8n.tudominio.com/webhook/..."
                />
              </div>
            )}
            
            {/* Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Máx. Acciones por Día</Label>
                <Input
                  type="number"
                  value={formData.max_actions_per_day}
                  onChange={e => setFormData(prev => ({ ...prev, max_actions_per_day: parseInt(e.target.value) || 50 }))}
                />
              </div>
              <div>
                <Label>Máx. Longitud de Mensaje</Label>
                <Input
                  type="number"
                  value={formData.max_message_length}
                  onChange={e => setFormData(prev => ({ ...prev, max_message_length: parseInt(e.target.value) || 1000 }))}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>
              Cancelar
            </Button>
            <Button 
              onClick={showEditModal ? handleUpdateAgent : handleCreateAgent}
              className="bg-amber-500 hover:bg-amber-600"
              disabled={!formData.name}
              data-testid="save-agent-btn"
            >
              <Save size={16} className="mr-2" />
              {showEditModal ? 'Guardar Cambios' : 'Crear Agente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Agent Chat Modal - Full Capabilities */}
      <Dialog open={showAgentChatModal} onOpenChange={setShowAgentChatModal}>
        <DialogContent className="max-w-2xl max-h-[min(800px,90vh)] flex flex-col">
          <DialogHeader className="flex-shrink-0 border-b pb-3">
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg flex items-center justify-center">
                <Bot className="text-amber-600" size={24} />
              </div>
              <div>
                <span className="text-lg">{selectedAgent?.name}</span>
                <p className="text-xs text-gray-500 font-normal">
                  Agente de {AGENT_TYPES.find(t => t.value === selectedAgent?.type)?.label || 'IA'}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 py-3 border-b">
            {chatQuickActions.map((action, i) => (
              <button
                key={i}
                onClick={() => {
                  setAgentChatInput(action.prompt);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-amber-100 rounded-full text-xs text-gray-700 hover:text-amber-700 transition-colors"
              >
                <action.icon size={12} />
                {action.label}
              </button>
            ))}
          </div>
          
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto py-4 space-y-4 min-h-[300px]">
            {agentChatMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-3 ${
                    msg.sender === 'user'
                      ? 'bg-amber-500 text-white'
                      : msg.error
                      ? 'bg-red-50 border border-red-200 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  {msg.action && (
                    <p className="mt-2 text-xs bg-green-500/20 text-green-100 px-2 py-1 rounded flex items-center gap-1">
                      <CheckCircle size={12} />
                      Acción ejecutada: {msg.action}
                    </p>
                  )}
                  <p className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-amber-200' : 'text-gray-400'}`}>
                    {msg.timestamp.toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {agentChatLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-4 py-3">
                  <Loader2 className="animate-spin text-amber-500" size={20} />
                </div>
              </div>
            )}
            <div ref={agentChatEndRef} />
          </div>
          
          {/* Chat Input */}
          <div className="border-t pt-4 flex gap-2">
            <Input
              value={agentChatInput}
              onChange={(e) => setAgentChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAgentChatSend()}
              placeholder="Escribe lo que necesitas..."
              className="flex-1"
              disabled={agentChatLoading}
            />
            <Button
              onClick={handleAgentChatSend}
              disabled={!agentChatInput.trim() || agentChatLoading}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {agentChatLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Action Detail Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="max-w-xl max-h-[min(800px,90vh)] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Detalle de Acción</DialogTitle>
          </DialogHeader>
          
          {selectedAction && (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              <div className="flex items-center gap-2">
                <Badge>{selectedAction.action_type}</Badge>
                <Badge variant={selectedAction.risk_level === 'high' ? 'destructive' : 'outline'}>
                  Riesgo {selectedAction.risk_level}
                </Badge>
              </div>
              
              {selectedAction.empresa_name && (
                <p className="text-sm">
                  <strong>Empresa:</strong> {selectedAction.empresa_name}
                </p>
              )}
              {selectedAction.contact_name && (
                <p className="text-sm">
                  <strong>Contacto:</strong> {selectedAction.contact_name} ({selectedAction.contact_email})
                </p>
              )}
              
              {selectedAction.content?.subject && (
                <div>
                  <Label>Asunto</Label>
                  <Input value={selectedAction.content.subject} readOnly />
                </div>
              )}
              
              {selectedAction.content?.body && (
                <div>
                  <Label>Contenido</Label>
                  <Textarea value={selectedAction.content.body} rows={6} readOnly />
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => handleApproveAction(selectedAction.id, true)}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Aprobar
                </Button>
                <Button
                  onClick={() => handleApproveAction(selectedAction.id, false)}
                  variant="destructive"
                  className="flex-1"
                >
                  <XCircle size={16} className="mr-2" />
                  Rechazar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Training Documents Modal */}
      <Dialog open={showTrainingModal} onOpenChange={setShowTrainingModal}>
        <DialogContent className="max-w-3xl max-h-[min(800px,90vh)] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="text-amber-500" />
              Documentos de Entrenamiento - {selectedAgent?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {/* Add New Training Document */}
            <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
              <h4 className="font-medium text-gray-800 flex items-center gap-2">
                <FileUp size={16} />
                Agregar Documento de Entrenamiento
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo de Material</Label>
                  <Select 
                    value={newTraining.type} 
                    onValueChange={val => setNewTraining(prev => ({ ...prev, type: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="document">📄 Documento de Referencia</SelectItem>
                      <SelectItem value="instruction">📋 Instrucción/Directiva</SelectItem>
                      <SelectItem value="email_example">✉️ Ejemplo de Email</SelectItem>
                      <SelectItem value="image_content">🖼️ Ejemplo de Imagen/Contenido</SelectItem>
                      <SelectItem value="message_example">💬 Ejemplo de Mensaje</SelectItem>
                      <SelectItem value="proposal_example">📑 Ejemplo de Propuesta</SelectItem>
                      <SelectItem value="faq">❓ FAQ/Pregunta Frecuente</SelectItem>
                      <SelectItem value="tone_style">🎨 Tono y Estilo</SelectItem>
                      <SelectItem value="do_not">🚫 Lo que NO debe hacer</SelectItem>
                      <SelectItem value="approved_action">✅ Acción Aprobada (Aprendizaje)</SelectItem>
                      <SelectItem value="rejected_action">❌ Acción Rechazada (Aprendizaje)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select 
                    value={newTraining.category} 
                    onValueChange={val => setNewTraining(prev => ({ ...prev, category: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="product">Productos/Servicios</SelectItem>
                      <SelectItem value="sales">Ventas</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="support">Soporte</SelectItem>
                      <SelectItem value="company">Información de Empresa</SelectItem>
                      <SelectItem value="pricing">Precios/Cotizaciones</SelectItem>
                      <SelectItem value="communication">Comunicación</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* File Upload Section */}
              <div className="border-2 border-dashed border-amber-300 rounded-lg p-4 bg-amber-50/50">
                <Label className="flex items-center gap-2 mb-2">
                  <FileUp size={16} className="text-amber-600" />
                  Subir Archivo al Drive del Agente (opcional)
                </Label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="training-file-upload"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.xls,.xlsx,.ppt,.pptx"
                />
                <div className="flex gap-2 items-center">
                  <label
                    htmlFor="training-file-upload"
                    className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-white border border-amber-400 rounded-md hover:bg-amber-100 transition-colors text-sm"
                  >
                    <Upload size={16} />
                    Seleccionar Archivo
                  </label>
                  {newTraining.file_name && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-md border">
                      <FileText size={14} className="text-amber-600" />
                      <span className="text-sm truncate max-w-[200px]">{newTraining.file_name}</span>
                      <button
                        onClick={() => setNewTraining(prev => ({ ...prev, file: null, file_name: '', file_type: '' }))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Formatos: PDF, Word, Excel, PowerPoint, Imágenes, TXT (máx. 10MB). El archivo se subirá a la carpeta del agente en Drive.
                </p>
              </div>
              
              <div>
                <Label>Título</Label>
                <Input
                  value={newTraining.title}
                  onChange={e => setNewTraining(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Título descriptivo del documento..."
                />
              </div>
              
              <div>
                <Label>
                  {newTraining.type === 'instruction' ? 'Instrucción para el agente' :
                   newTraining.type === 'email_example' ? 'Ejemplo de email (asunto y cuerpo)' :
                   newTraining.type === 'image_content' ? 'Descripción del tipo de imagen/contenido' :
                   newTraining.type === 'message_example' ? 'Ejemplo de mensaje de WhatsApp/SMS' :
                   newTraining.type === 'proposal_example' ? 'Estructura y contenido de propuesta' :
                   newTraining.type === 'faq' ? 'Pregunta y respuesta' :
                   newTraining.type === 'tone_style' ? 'Descripción del tono y estilo deseado' :
                   newTraining.type === 'do_not' ? 'Qué NO debe hacer el agente' :
                   newTraining.type === 'approved_action' ? 'Describe la acción aprobada (el agente aprenderá de esto)' :
                   newTraining.type === 'rejected_action' ? 'Describe la acción rechazada y por qué (para evitar repetir)' :
                   'Contenido del documento'}
                </Label>
                <Textarea
                  value={newTraining.content}
                  onChange={e => setNewTraining(prev => ({ ...prev, content: e.target.value }))}
                  placeholder={
                    newTraining.type === 'instruction' ? 'Ej: Siempre saluda al cliente por su nombre y menciona que representas a Infinito Televisión...' :
                    newTraining.type === 'email_example' ? 'Asunto: Propuesta de servicios\n\nEstimado {{contacto.nombre}},\n\nNos complace...' :
                    newTraining.type === 'image_content' ? 'Para marketing de eventos: usar colores vibrantes, incluir logo, mostrar equipos de producción...' :
                    newTraining.type === 'message_example' ? '¡Hola {{contacto.nombre}}! 👋 Soy del equipo de Infinito TV. ¿Tienes 5 minutos para...' :
                    newTraining.type === 'faq' ? 'P: ¿Cuál es el horario de atención?\nR: Nuestro horario es de lunes a viernes, 8am a 6pm...' :
                    newTraining.type === 'do_not' ? 'No mencionar precios sin consultar primero. No prometer fechas de entrega sin confirmación...' :
                    'Contenido del documento de entrenamiento...'
                  }
                  rows={6}
                />
              </div>
              
              {/* Type-specific tips */}
              {newTraining.type && (
                <div className="text-xs p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <strong className="text-blue-700">💡 Tip:</strong>{' '}
                  <span className="text-blue-600">
                    {newTraining.type === 'instruction' && 'Las instrucciones son directivas que el agente seguirá en todas sus interacciones.'}
                    {newTraining.type === 'email_example' && 'Incluye el asunto y cuerpo completo. El agente usará esto como referencia de estilo.'}
                    {newTraining.type === 'image_content' && 'Describe qué tipo de imágenes debe generar para marketing, propuestas, etc.'}
                    {newTraining.type === 'message_example' && 'Incluye emojis y el tono que deseas. El agente imitará este estilo.'}
                    {newTraining.type === 'proposal_example' && 'Incluye secciones típicas: introducción, servicios, precios, términos.'}
                    {newTraining.type === 'faq' && 'Formato: Pregunta seguida de respuesta. Ayuda al agente a responder consultas comunes.'}
                    {newTraining.type === 'tone_style' && 'Define si debe ser formal, casual, técnico, amigable, etc.'}
                    {newTraining.type === 'do_not' && 'Importante: el agente evitará estas acciones/frases en el futuro.'}
                    {newTraining.type === 'approved_action' && 'Esta acción fue exitosa. El agente aprenderá a replicar este patrón.'}
                    {newTraining.type === 'rejected_action' && 'Esta acción fue rechazada. El agente aprenderá a evitar este patrón.'}
                    {newTraining.type === 'document' && 'Información de referencia que el agente consultará al generar contenido.'}
                  </span>
                </div>
              )}
              
              <Button 
                onClick={handleAddTraining}
                className="w-full bg-amber-500 hover:bg-amber-600"
                disabled={(!newTraining.content && !newTraining.file) || uploadingFile}
              >
                {uploadingFile ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Subiendo archivo al Drive...
                  </>
                ) : (
                  <>
                    <Plus size={16} className="mr-2" />
                    {newTraining.file ? 'Subir Archivo al Drive' : 'Agregar Documento'}
                  </>
                )}
              </Button>
            </div>
            
            {/* Existing Training Documents */}
            <div>
              <h4 className="font-medium text-gray-800 mb-3">
                Documentos Existentes ({trainingData.length})
              </h4>
              
              {loadingTraining ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-amber-500" size={32} />
                </div>
              ) : trainingData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen size={40} className="mx-auto mb-2 opacity-50" />
                  <p>No hay documentos de entrenamiento</p>
                  <p className="text-sm">Agrega documentos para mejorar las respuestas del agente</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {trainingData.map(doc => (
                    <div 
                      key={doc.id}
                      className="bg-white p-3 rounded-lg border border-gray-200 hover:border-amber-200 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {doc.type === 'document' && 'Documento'}
                              {doc.type === 'example' && 'Ejemplo'}
                              {doc.type === 'instruction' && 'Instrucción'}
                              {doc.type === 'faq' && 'FAQ'}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {doc.category}
                            </Badge>
                          </div>
                          <h5 className="font-medium text-gray-900">
                            {doc.title || 'Sin título'}
                          </h5>
                          <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                            {doc.content}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(doc.created_at).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteTraining(doc.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowTrainingModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Media Generation Modal (Nano Banana & Google Veo) */}
      <Dialog open={showMediaModal} onOpenChange={setShowMediaModal}>
        <DialogContent className="max-w-2xl max-h-[min(800px,90vh)] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {mediaType === 'image' ? (
                <>
                  <Image className="text-purple-500" />
                  Generar Imagen con Nano Banana
                </>
              ) : (
                <>
                  <Video className="text-blue-500" />
                  Generar Video con Google Veo
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {/* Media Type Toggle */}
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => { setMediaType('image'); setGeneratedMedia(null); }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  mediaType === 'image' 
                    ? 'bg-white text-purple-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Image size={16} />
                Imagen (Nano Banana)
              </button>
              <button
                onClick={() => { setMediaType('video'); setGeneratedMedia(null); }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  mediaType === 'video' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Video size={16} />
                Video (Google Veo)
              </button>
            </div>
            
            {/* Info Box */}
            <div className={`p-3 rounded-lg border ${
              mediaType === 'image' 
                ? 'bg-purple-50 border-purple-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <p className={`text-sm ${mediaType === 'image' ? 'text-purple-700' : 'text-blue-700'}`}>
                {mediaType === 'image' ? (
                  <>
                    <strong>Nano Banana</strong> genera imágenes de alta calidad a partir de descripciones de texto. 
                    Ideal para contenido de marketing, ilustraciones y creativos.
                  </>
                ) : (
                  <>
                    <strong>Google Veo</strong> genera videos cortos a partir de descripciones de texto. 
                    La generación puede tardar varios minutos. Ideal para demos, clips promocionales y contenido visual.
                  </>
                )}
              </p>
            </div>
            
            {/* Prompt Input */}
            <div>
              <Label className="text-base font-medium">
                Describe {mediaType === 'image' ? 'la imagen' : 'el video'} que deseas generar
              </Label>
              <Textarea
                value={mediaPrompt}
                onChange={e => setMediaPrompt(e.target.value)}
                placeholder={mediaType === 'image' 
                  ? "Ej: Una imagen profesional de un equipo de producción de televisión trabajando en un set moderno con luces cinematográficas..."
                  : "Ej: Un video corto mostrando equipos de producción de video en acción, cámaras profesionales, luces de estudio, ambiente dinámico..."
                }
                rows={4}
                className="mt-2"
                data-testid="media-prompt-input"
              />
            </div>
            
            {/* Example Prompts */}
            <div>
              <Label className="text-sm text-gray-500">Ejemplos de prompts:</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {(mediaType === 'image' ? [
                  "Logo profesional para empresa de televisión",
                  "Banner promocional para evento en vivo",
                  "Imagen de equipo de producción audiovisual",
                  "Fondo abstracto con tema de comunicación"
                ] : [
                  "Intro animada para programa de TV",
                  "Demo de servicios de producción audiovisual",
                  "Video promocional para evento especial",
                  "Transición con efectos de cámara profesional"
                ]).map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setMediaPrompt(example)}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Generate Button */}
            <Button
              onClick={handleGenerateMedia}
              disabled={generatingMedia || !mediaPrompt.trim()}
              className={`w-full ${
                mediaType === 'image' 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' 
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
              }`}
              data-testid="generate-media-btn"
            >
              {generatingMedia ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  {mediaType === 'image' ? 'Generando imagen...' : 'Iniciando generación de video...'}
                </>
              ) : (
                <>
                  <Wand2 size={16} className="mr-2" />
                  Generar {mediaType === 'image' ? 'Imagen' : 'Video'}
                </>
              )}
            </Button>
            
            {/* Generated Media Result */}
            {generatedMedia && (
              <div className={`mt-4 p-4 rounded-lg border ${
                mediaType === 'image' 
                  ? 'bg-purple-50 border-purple-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <h4 className={`font-medium mb-3 flex items-center gap-2 ${
                  mediaType === 'image' ? 'text-purple-800' : 'text-blue-800'
                }`}>
                  {mediaType === 'image' ? (
                    <><Image size={18} /> Imagen Generada</>
                  ) : (
                    <><Video size={18} /> Video {generatedMedia.status === 'processing' ? 'en Proceso' : 'Generado'}</>
                  )}
                </h4>
                
                {/* Image Result */}
                {mediaType === 'image' && generatedMedia.data_url && (
                  <div className="space-y-3">
                    <img 
                      src={generatedMedia.data_url} 
                      alt="Generated" 
                      className="w-full max-h-[400px] object-contain rounded-lg border border-purple-200"
                    />
                    {generatedMedia.text_response && (
                      <p className="text-sm text-gray-600 italic">
                        {generatedMedia.text_response}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <a 
                        href={generatedMedia.data_url} 
                        download={`imagen_${generatedMedia.image_id}.png`}
                        className="flex-1"
                      >
                        <Button variant="outline" className="w-full">
                          Descargar Imagen
                        </Button>
                      </a>
                    </div>
                  </div>
                )}
                
                {/* Video Result */}
                {mediaType === 'video' && (
                  <div className="space-y-3">
                    {generatedMedia.status === 'processing' ? (
                      <div className="text-center py-8">
                        <Loader2 size={40} className="mx-auto animate-spin text-blue-500 mb-4" />
                        <p className="text-blue-700 font-medium">Video en proceso de generación</p>
                        <p className="text-sm text-blue-600 mt-1">
                          Este proceso puede tardar varios minutos. El video se enviará a Telegram para tu evaluación cuando esté listo.
                        </p>
                        <p className="text-xs text-gray-500 mt-3">
                          ID: {generatedMedia.video_id}
                        </p>
                      </div>
                    ) : generatedMedia.video_url ? (
                      <div className="space-y-3">
                        <video 
                          controls 
                          className="w-full max-h-[400px] rounded-lg border border-blue-200"
                          src={generatedMedia.video_url}
                        >
                          Tu navegador no soporta videos HTML5
                        </video>
                        {generatedMedia.duration_seconds && (
                          <p className="text-sm text-gray-600">
                            Duración: {generatedMedia.duration_seconds} segundos
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                        <p className="text-green-700">Generación iniciada exitosamente</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Recibirás el video en Telegram cuando esté listo.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowMediaModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Email Templates Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-3xl max-h-[min(850px,90vh)] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <MailPlus className="text-emerald-500" />
              Enviar Email con Template
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {/* Template Selection */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-base font-medium">Seleccionar Template</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={openTemplateAssistant}
                  className="text-purple-600 hover:bg-purple-50"
                >
                  <Sparkle size={14} className="mr-1" />
                  Crear con AI
                </Button>
              </div>
              <Select
                value={emailForm.template_id}
                onValueChange={handleTemplateSelect}
              >
                <SelectTrigger data-testid="template-select">
                  <SelectValue placeholder="Selecciona un template (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin template (personalizado)</SelectItem>
                  {emailTemplates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        <span>{template.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {template.category}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Template Info */}
            {selectedTemplate && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-700">
                  <strong>{selectedTemplate.name}</strong> - {selectedTemplate.category}
                </p>
                {selectedTemplate.attachments?.length > 0 && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <Paperclip size={12} />
                    {selectedTemplate.attachments.length} archivo(s) adjunto(s) incluido(s)
                  </p>
                )}
              </div>
            )}
            
            {/* Empresa Selection */}
            <div>
              <Label className="text-base font-medium">Empresa destinataria *</Label>
              <EmpresaSearchSelect
                value={emailForm.empresa_id}
                onChange={(id) => setEmailForm(prev => ({ ...prev, empresa_id: id, contact_id: '' }))}
                empresas={empresas}
                placeholder="Buscar empresa..."
                className="mt-2"
              />
            </div>
            
            {/* Contact Selection */}
            {emailForm.empresa_id && (
              <div>
                <Label>Contacto (opcional)</Label>
                <Select
                  value={emailForm.contact_id || "none"}
                  onValueChange={(val) => setEmailForm(prev => ({ ...prev, contact_id: val === "none" ? "" : val }))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Seleccionar contacto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Email de la empresa</SelectItem>
                    {empresas.find(e => e.id === emailForm.empresa_id)?.contacts?.map(contact => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} ({contact.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Subject */}
            <div>
              <Label className="text-base font-medium">Asunto</Label>
              <Input
                value={emailForm.subject}
                onChange={e => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Asunto del email..."
                className="mt-2"
                data-testid="email-subject"
              />
            </div>
            
            {/* Body */}
            <div>
              <Label className="text-base font-medium">Contenido del email</Label>
              <Textarea
                value={emailForm.body}
                onChange={e => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Escribe el contenido del email... Puedes usar variables como {{empresa.nombre}}, {{contacto.nombre}}, etc."
                rows={8}
                className="mt-2 font-mono text-sm"
                data-testid="email-body"
              />
            </div>
            
            {/* Variables Help */}
            <div className="border rounded-lg p-3 bg-gray-50">
              <Label className="text-sm text-gray-600 flex items-center gap-1 mb-2">
                <FileText size={14} />
                Variables disponibles (se reemplazan automáticamente)
              </Label>
              <div className="flex flex-wrap gap-1">
                {templateVariables.slice(0, 8).map((v, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setEmailForm(prev => ({
                        ...prev,
                        body: prev.body + v.name
                      }));
                    }}
                    className="text-xs px-2 py-1 bg-white border rounded hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                    title={v.description}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Attachments Options */}
            {selectedTemplate?.attachments?.length > 0 && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={emailForm.include_template_attachments}
                  onCheckedChange={(checked) => setEmailForm(prev => ({ ...prev, include_template_attachments: checked }))}
                />
                <Label className="text-sm">Incluir archivos adjuntos del template</Label>
              </div>
            )}
            
            {/* Send Button */}
            <Button
              onClick={handleSendTemplateEmail}
              disabled={sendingEmail || !emailForm.empresa_id}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Enviando email...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Enviar Email
                </>
              )}
            </Button>
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Template Assistant Modal */}
      <Dialog open={showTemplateAssistant} onOpenChange={setShowTemplateAssistant}>
        <DialogContent className="max-w-2xl max-h-[min(850px,90vh)] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkle className="text-purple-500" />
              Asistente de Creación de Templates
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {!generatedTemplate ? (
              <>
                {/* Template Type */}
                <div>
                  <Label className="text-base font-medium">Tipo de Template</Label>
                  <Select
                    value={assistantForm.template_type}
                    onValueChange={(val) => setAssistantForm(prev => ({ ...prev, template_type: val }))}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bienvenida">Bienvenida</SelectItem>
                      <SelectItem value="seguimiento">Seguimiento</SelectItem>
                      <SelectItem value="propuesta">Propuesta</SelectItem>
                      <SelectItem value="agradecimiento">Agradecimiento</SelectItem>
                      <SelectItem value="promoción">Promoción</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Purpose */}
                <div>
                  <Label className="text-base font-medium">Propósito del Template *</Label>
                  <Textarea
                    value={assistantForm.purpose}
                    onChange={e => setAssistantForm(prev => ({ ...prev, purpose: e.target.value }))}
                    placeholder="Describe qué debe lograr este template. Ej: 'Enviar propuesta de servicios de producción de video para eventos corporativos con precios y paquetes disponibles'"
                    rows={3}
                    className="mt-2"
                  />
                </div>
                
                {/* Tone */}
                <div>
                  <Label className="text-base font-medium">Tono</Label>
                  <Select
                    value={assistantForm.tone}
                    onValueChange={(val) => setAssistantForm(prev => ({ ...prev, tone: val }))}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="profesional">Profesional</SelectItem>
                      <SelectItem value="amigable">Amigable</SelectItem>
                      <SelectItem value="formal">Formal</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Target Audience */}
                <div>
                  <Label className="text-base font-medium">Audiencia Objetivo</Label>
                  <Input
                    value={assistantForm.target_audience}
                    onChange={e => setAssistantForm(prev => ({ ...prev, target_audience: e.target.value }))}
                    placeholder="Ej: Directores de marketing de empresas medianas"
                    className="mt-2"
                  />
                </div>
                
                {/* Key Points */}
                <div>
                  <Label className="text-base font-medium">Puntos Clave a Incluir</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newKeyPoint}
                      onChange={e => setNewKeyPoint(e.target.value)}
                      placeholder="Agregar punto clave..."
                      onKeyPress={e => e.key === 'Enter' && addKeyPoint()}
                    />
                    <Button type="button" onClick={addKeyPoint} variant="outline">
                      <Plus size={16} />
                    </Button>
                  </div>
                  {assistantForm.key_points.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {assistantForm.key_points.map((point, idx) => (
                        <Badge key={idx} variant="secondary" className="gap-1">
                          {point}
                          <button onClick={() => removeKeyPoint(idx)} className="ml-1">
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Generate Button */}
                <Button
                  onClick={handleGenerateTemplate}
                  disabled={generatingTemplate || !assistantForm.purpose.trim()}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {generatingTemplate ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Generando template con AI...
                    </>
                  ) : (
                    <>
                      <Wand2 size={16} className="mr-2" />
                      Generar Template
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                {/* Generated Template Preview */}
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-2">{generatedTemplate.name}</h4>
                  <Badge className="mb-3">{generatedTemplate.category}</Badge>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm text-purple-700">Asunto:</Label>
                      <Input
                        value={generatedTemplate.subject}
                        onChange={e => setGeneratedTemplate(prev => ({ ...prev, subject: e.target.value }))}
                        className="mt-1 bg-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm text-purple-700">Contenido:</Label>
                      <Textarea
                        value={generatedTemplate.body}
                        onChange={e => setGeneratedTemplate(prev => ({ ...prev, body: e.target.value }))}
                        rows={10}
                        className="mt-1 bg-white font-mono text-sm"
                      />
                    </div>
                    
                    {generatedTemplate.variables?.length > 0 && (
                      <div>
                        <Label className="text-sm text-purple-700">Variables usadas:</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {generatedTemplate.variables.map((v, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {v}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {generatedTemplate.tips?.length > 0 && (
                      <div className="mt-3 p-2 bg-white rounded border">
                        <Label className="text-sm text-purple-700 flex items-center gap-1">
                          <AlertCircle size={14} />
                          Tips para usar este template:
                        </Label>
                        <ul className="text-xs text-gray-600 mt-1 list-disc list-inside">
                          {generatedTemplate.tips.map((tip, idx) => (
                            <li key={idx}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => setGeneratedTemplate(null)}
                    variant="outline"
                    className="flex-1"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Generar otro
                  </Button>
                  <Button
                    onClick={handleSaveGeneratedTemplate}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  >
                    <Save size={16} className="mr-2" />
                    Guardar Template
                  </Button>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowTemplateAssistant(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentsPage;
